import {
  CategorieVehicule,
  CategorieVehiculeBus,
  CategorieVehiculeLourd,
  ClientType,
  NouveauDossierConcessionnaireData,
  usageLourdParCategories,
  usageParticulierParCategories,
  UsageVehiculeParticulier,
  VehiculeType,
} from 'types/Concessionnaire';
import { M1_CONDITIONAL_QUESTION_NAMES } from 'views/dossiers/concessionnaire/steps/step2/step2.rules';

interface QuestionsSectionFieldsArgs {
  personnePhysique: boolean;
  categorieVehicule?:
    | CategorieVehicule
    | CategorieVehiculeBus
    | CategorieVehiculeLourd;
  usageVehicule?: UsageVehiculeParticulier;
}

interface ShowM1ConditionalQuestionsArgs {
  personnePhysique: boolean;
  categorieVehicule?:
    | CategorieVehicule
    | CategorieVehiculeBus
    | CategorieVehiculeLourd;
  usageVehicule?: UsageVehiculeParticulier;
}

interface UsageOptionsArgs {
  personnePhysique: boolean;
  isVehiculeLourd: boolean;
  categorieVehicule?:
    | CategorieVehicule
    | CategorieVehiculeBus
    | CategorieVehiculeLourd;
}

interface VehiculeCategoryOptionsArgs {
  personnePhysique: boolean;
  isVehiculeLourdValue: boolean;
  isBusValue: boolean;
}

export const PERSONNE_MORALE_LEGER_CATEGORY_OPTIONS = [
  CategorieVehicule.M1,
  CategorieVehicule.VUL_MOINS_1_55,
  CategorieVehicule.VUL_ENTRE_1_55_ET_2,
  CategorieVehicule.VUL_PLUS_2,
] as const;

export const PERSONNE_PHYSIQUE_CATEGORY_OPTIONS = [
  CategorieVehicule.M1,
  CategorieVehicule.VUL_MOINS_1_55,
  CategorieVehicule.VUL_ENTRE_1_55_ET_2,
  CategorieVehicule.VUL_PLUS_2,
] as const;

export const PERSONNE_PHYSIQUE_VUL_CATEGORY_LABELS: Partial<
  Record<CategorieVehicule, string>
> = {
  [CategorieVehicule.VUL_MOINS_1_55]:
    'N1,N2 avec dérogation du poids ≤ 1,55 tonne',
  [CategorieVehicule.VUL_ENTRE_1_55_ET_2]:
    'N1,N2 avec dérogation du poids > 1,55 tonne et ≤ 2 tonnes',
  [CategorieVehicule.VUL_PLUS_2]: 'N1,N2 avec dérogation du poids > 2 tonnes',
};

export function isPersonnePhysiqueVulCategory(
  categorieVehicule?:
    | CategorieVehicule
    | CategorieVehiculeBus
    | CategorieVehiculeLourd,
) {
  return (
    PERSONNE_PHYSIQUE_CATEGORY_OPTIONS.slice(1) as readonly CategorieVehicule[]
  ).includes(categorieVehicule as CategorieVehicule);
}

export function isPersonnePhysique(typeClient?: ClientType) {
  return typeClient === ClientType.PERSONNE_PHYSIQUE;
}

export function isVehiculeLourd(vehiculeType?: VehiculeType) {
  return !!vehiculeType && vehiculeType !== VehiculeType.LEGER;
}

export function isBus(vehiculeType?: VehiculeType) {
  return vehiculeType === VehiculeType.BUS;
}

export function getCommandeSectionFields(personnePhysique: boolean) {
  return personnePhysique
    ? ([
        'nbDevis',
        'dateEditionDevis',
        'datePrevisionnelleDevis',
        'devis',
      ] as (keyof NouveauDossierConcessionnaireData)[])
    : ([] as (keyof NouveauDossierConcessionnaireData)[]);
}

export function getQuestionsSectionFields({
  personnePhysique,
  categorieVehicule,
  usageVehicule,
}: QuestionsSectionFieldsArgs) {
  if (!personnePhysique) {
    return [] as (keyof NouveauDossierConcessionnaireData)[];
  }

  const fields: (keyof NouveauDossierConcessionnaireData)[] = [
    'categorieVehicule',
    'dateLivraison',
  ];

  if (categorieVehicule) {
    fields.push('usageVehiculeParticulier');
  }

  if (
    shouldShowM1ConditionalQuestions({
      personnePhysique,
      categorieVehicule,
      usageVehicule,
    })
  ) {
    fields.push(...M1_CONDITIONAL_QUESTION_NAMES);
  }

  if (
    shouldShowVulFabricationEuropeQuestion({
      personnePhysique,
      categorieVehicule,
    })
  ) {
    fields.push('produitEnEurope');
  }

  return fields;
}

export function shouldShowM1ConditionalQuestions({
  personnePhysique,
  categorieVehicule,
  usageVehicule,
}: ShowM1ConditionalQuestionsArgs) {
  return (
    personnePhysique &&
    categorieVehicule === CategorieVehicule.M1 &&
    [
      UsageVehiculeParticulier.ACHAT,
      UsageVehiculeParticulier.LOCATION,
    ].includes(usageVehicule as UsageVehiculeParticulier)
  );
}

export function shouldShowVulFabricationEuropeQuestion({
  personnePhysique,
  categorieVehicule,
}: Pick<
  ShowM1ConditionalQuestionsArgs,
  'personnePhysique' | 'categorieVehicule'
>) {
  return personnePhysique && isPersonnePhysiqueVulCategory(categorieVehicule);
}

export function getUsageOptions({
  personnePhysique,
  isVehiculeLourd,
  categorieVehicule,
}: UsageOptionsArgs): string[] {
  if (!categorieVehicule) {
    return [];
  }

  if (isVehiculeLourd) {
    return (
      usageLourdParCategories[
        categorieVehicule as keyof typeof usageLourdParCategories
      ] ?? []
    );
  }

  if (personnePhysique) {
    return (
      usageParticulierParCategories[
        categorieVehicule as keyof typeof usageParticulierParCategories
      ] ?? []
    );
  }

  return [];
}

export function getVehiculeCategoryOptions({
  personnePhysique,
  isVehiculeLourdValue,
  isBusValue,
}: VehiculeCategoryOptionsArgs) {
  if (!isVehiculeLourdValue) {
    return personnePhysique
      ? PERSONNE_PHYSIQUE_CATEGORY_OPTIONS
      : PERSONNE_MORALE_LEGER_CATEGORY_OPTIONS;
  }

  return Object.values(
    isBusValue ? CategorieVehiculeBus : CategorieVehiculeLourd,
  );
}
